import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Button,
} from '@material-ui/core'
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
} from '@material-ui/icons'
import { Document, Page, pdfjs } from 'react-pdf'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'

// Set up PDF.js worker
const env = process.env.NODE_ENV
pdfjs.GlobalWorkerOptions.workerSrc =
  env === 'development'
    ? `https://unpkg.com/pdfjs-dist@2.12.313/build/pdf.worker.min.js`
    : `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${
        pdfjs.version
      }/pdf.worker.min.js`

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      maxHeight: '800px',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  closeButton: {
    padding: theme.spacing(1),
  },
  content: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  previewContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    overflow: 'auto',
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  pdfPage: {
    border: '1px solid #ddd',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    color: theme.palette.error.main,
  },
  errorText: {
    textAlign: 'center',
    fontSize: '1rem',
  },
  htmlPreview: {
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: 'white',
  },
  pageNavigation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: 'white',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  pageButton: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    border: 'none',
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
      cursor: 'not-allowed',
    },
  },
  pageInfo: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
}))

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   designData: {
 *     htmlContent?: string,
 *     s3CmykLink?: string,
 *     thumbnail?: string,
 *     imageSrc?: string,
 *     thumbnailUrl?: string,
 *     s3Link?: string,
 *     fileName?: string,
 *     format?: string,
 *     createdAt?: string,
 *     author?: {
 *       firstName: string,
 *       lastName: string,
 *     },
 *   },
 *   designName: string,
 * }} props
 */
const DesignPreviewModal = ({
  isOpen,
  onClose,
  designData,
  designName = 'Design Preview',
}) => {
  const classes = useStyles()
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfData, setPdfData] = useState(null)
  const [fileType, setFileType] = useState(null)
  const safeDesignData = useMemo(() => designData || {}, [designData])
  const createdAt = useMemo(
    () =>
      safeDesignData.createdAt
        ? moment(Number(safeDesignData.createdAt))
            .local()
            .format('L')
        : '-',
    [safeDesignData.createdAt],
  )
  const authorName =
    safeDesignData.author &&
    (safeDesignData.author.firstName || safeDesignData.author.lastName)
      ? `${safeDesignData.author.firstName || ''} ${safeDesignData.author
          .lastName || ''}`.trim()
      : '-'
  // Use priority: s3CmykLink > s3Link > thumbnailUrl > imageSrc
  const pdfUrl =
    safeDesignData.s3CmykLink ||
    safeDesignData.s3Link ||
    safeDesignData.thumbnailUrl ||
    safeDesignData.imageSrc ||
    safeDesignData.thumbnail

  useEffect(() => {
    if (isOpen && safeDesignData) {
      setLoading(true)
      setError(null)
      setPageNumber(1)
      if (safeDesignData.htmlContent) {
        // For HTML content, we'll display it in an iframe
        setLoading(false)
      } else {
        if (pdfUrl) {
          // Try to detect file type from URL extension first
          const urlParts = pdfUrl.split('?')[0] // Remove query params
          const extension = urlParts
            .split('.')
            .pop()
            .toLowerCase()
          let detectedType = 'pdf'

          if (
            ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)
          ) {
            detectedType = 'image'
          }

          setFileType(detectedType)

          // For images or if it's a thumbnail, use URL directly
          if (detectedType === 'image' || pdfUrl.includes('thumbnail')) {
            setPdfData(pdfUrl)
            setLoading(false)
          } else {
            // For PDFs, try multiple approaches
            // First, try to fetch with CORS mode
            fetch(pdfUrl, {
              method: 'GET',
              mode: 'cors',
              credentials: 'omit',
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`)
                }
                return response.arrayBuffer()
              })
              .then(arrayBuffer => {
                setPdfData(arrayBuffer)
                setLoading(false)
              })
              .catch(err => {
                // If CORS fails, try opening in a new window as fallback
                console.warn('Failed to fetch PDF, trying direct URL:', err)
                // Try using the URL directly in case it works with embed
                setPdfData(pdfUrl)
                setFileType('pdf-url')
                setLoading(false)
              })
          }
        } else if (safeDesignData.thumbnailUrl || safeDesignData.imageSrc) {
          // Fallback to thumbnail if no PDF is available
          const imageUrl =
            safeDesignData.thumbnailUrl || safeDesignData.imageSrc
          setPdfData(imageUrl)
          setFileType('image')
          setLoading(false)
        } else {
          setError('No preview data available')
          setLoading(false)
        }
      }
    }
  }, [isOpen, safeDesignData])

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setLoading(false)
  }

  const onDocumentLoadError = error => {
    setError('Failed to load PDF document')
    setLoading(false)
  }

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages))
  }

  const injectFlexToBody = htmlContent => {
    // Check if the HTML already has a head tag
    if (htmlContent.includes('<head>')) {
      // Inject the style into the existing head
      return htmlContent.replace(
        '<head>',
        '<head><style>body { display: flex !important; gap: 10px !important; }</style>',
      )
    } else if (htmlContent.includes('<html>')) {
      // Add a head with the style after the html opening tag
      return htmlContent.replace(
        '<html>',
        '<html><head><style>body { display: flex !important; gap: 10px !important; }</style></head>',
      )
    } else {
      // Wrap the content and add the head with style
      return `<html><head><style>body { display: flex !important; gap: 10px !important; }</style></head><body>${htmlContent}</body></html>`
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <span>Loading preview...</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className={classes.errorContainer}>
          <ErrorOutlineIcon fontSize="large" />
          <div className={classes.errorText}>{error}</div>
        </div>
      )
    }

    if (safeDesignData.htmlContent) {
      // Inject display: flex to body tag and create a blob URL for the HTML content
      const modifiedHtmlContent = injectFlexToBody(safeDesignData.htmlContent)
      const htmlBlob = new Blob([modifiedHtmlContent], { type: 'text/html' })
      const htmlUrl = URL.createObjectURL(htmlBlob)

      return (
        <iframe
          src={htmlUrl}
          className={classes.htmlPreview}
          title="Design Preview"
          allow="clipboard-read; clipboard-write; *"
          onLoad={e => {
            try {
              const iframe = e.target
              const iframeDoc =
                iframe.contentDocument || iframe.contentWindow.document
              const body = iframeDoc.body

              const contentWidth = body.scrollWidth
              const contentHeight = body.scrollHeight
              const iframeWidth = iframe.clientWidth
              const iframeHeight = iframe.clientHeight

              if (contentWidth > iframeWidth || contentHeight > iframeHeight) {
                const scale = Math.min(
                  iframeWidth / contentWidth,
                  iframeHeight / contentHeight,
                )
                body.style.transformOrigin = 'top left'
                body.style.transform = `scale(${scale})`
                iframeDoc.documentElement.style.overflow = 'hidden'
              }
            } catch (err) {
              console.warn('Could not scale iframe content:', err)
            }
            setTimeout(() => URL.revokeObjectURL(htmlUrl), 1000)
          }}
        />
      )
    }

    if (pdfData) {
      if (fileType === 'image') {
        return (
          <div className={classes.pdfContainer}>
            <img
              src={pdfData}
              alt="Design Preview"
              className={classes.pdfPage}
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
              }}
              onError={e => {
                setError('Failed to load image preview')
                e.target.style.display = 'none'
              }}
            />
          </div>
        )
      } else if (fileType === 'pdf-url') {
        // Try to embed the PDF directly using iframe
        return (
          <div style={{ width: '100%', height: '100%' }}>
            <iframe
              src={pdfData}
              className={classes.htmlPreview}
              title="PDF Preview"
              style={{ width: '100%', height: '100%' }}
              allow="clipboard-read; clipboard-write; *"
              onError={() => {
                setError(
                  'Unable to display PDF. The file may need to be downloaded.',
                )
              }}
            />
            <div
              style={{
                padding: '10px',
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
              }}
            >
              <a
                href={pdfData}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline' }}
              >
                Open PDF in new tab
              </a>
            </div>
          </div>
        )
      } else {
        return (
          <div>
            <div className={classes.pdfContainer}>
              <Document
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<CircularProgress />}
              >
                <Page
                  pageNumber={pageNumber}
                  className={classes.pdfPage}
                  width={Math.min(800, window.innerWidth * 0.8)}
                />
              </Document>
            </div>
            {numPages > 1 && (
              <div className={classes.pageNavigation}>
                <button
                  className={classes.pageButton}
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                >
                  Previous
                </button>
                <span className={classes.pageInfo}>
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  className={classes.pageButton}
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )
      }
    }

    return (
      <div className={classes.errorContainer}>
        <ErrorOutlineIcon fontSize="large" />
        <div className={classes.errorText}>No preview available</div>
      </div>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <DialogTitle>
        <div className={classes.header}>
          <Typography className={classes.title}>{designName}</Typography>
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Typography>
          Format: {safeDesignData.format || '-'} • Author: {authorName}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.previewContainer}>{renderContent()}</div>
      </DialogContent>
      <DialogActions>
        <Box
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography>created: {createdAt}</Typography>
          {pdfUrl && (
            <Button
              component="a"
              startIcon={<SystemUpdateAltIcon />}
              href={pdfUrl}
              target="_blank"
              download
            >
              Download
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default DesignPreviewModal
